import { useQuery } from '@tanstack/react-query'
import { customerTicketQuery, type CustomerTicketAttachment, fetchCustomerComment } from '../api/easeedeskCustomerTicket'
import { Spinner } from '../ui/Spinner'
import { TicketChangesTimeline } from './TicketChanges'
import './TicketChanges.css'

/**
 * Tichetul vazut de CLIENT - `Easeedesk::customer_ticket_details`, macheta
 * `easeedesk/ticket/customer_ticket_details/index.php`.
 *
 * Pagina se deschide DIN EMAIL, de un om care nu are cont: nu are antet, nu
 * are meniuri si nu duce nicaieri in aplicatie. De-aia se deseneaza pe langa
 * scheletul obisnuit (vezi `Shell`), exact ca `staticTemplate` in portal.
 *
 * Ce apara pagina e jetonul din adresa, verificat pe server exact ca in portal
 * (vezi `App\Legacy\TicketLinkCrypt`): se descifreaza cu EncryptV2, si ce iese
 * trebuie sa fie NUMAI cifre si mai mare decat zero. Cand nu e, se arata
 * aceeasi pagina ca acolo - "linkul asta nu mai e bun" - nu un ecran gol.
 *
 * Panoul din dreapta - "All operations" - se deseneaza ACUM. In portal nu s-a
 * desenat niciodata: macheta `changes_customer.php` cere un constructor
 * javascript `TicketChangesCustomer` care nu e scris nicaieri in repo, deci
 * scriptul se oprea si jumatatea din dreapta ramanea alba. Vezi
 * `App\Easeedesk\Customer\CustomerTicket`.
 */

/* Cele 14 coloane ale machetei, in ordinea de acolo. */
const COLUMNS = [
  '#',
  'Type',
  'Subject',
  'Company',
  'Office',
  'Affected User/Device',
  'Priority',
  'Category',
  'Created by',
  'Assigned Group',
  'Assignee',
  'Created',
  'Updated',
  'Status',
]

/** `convert_bytes()` din helperul portalului. */
function bytes(size: number): string {
  if (size < 1024) return `${size} B`
  const units = ['KB', 'MB', 'GB']
  let value = size / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(2)} ${units[unit]}`
}

/**
 * Pagina din portal cand linkul nu mai e bun, cuvant cu cuvant si masura cu
 * masura: acolo era HTML scris de mana in controller, cu 410.
 */
function LinkGone() {
  return (
    <div
      style={{
        margin: 0,
        font: '16px/1.55 system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
        background: '#f4f6f8',
        color: '#22303f',
        minHeight: '100vh',
      }}
    >
      <div
        style={{
          maxWidth: '34rem',
          margin: '12vh auto',
          padding: '2rem',
          background: '#fff',
          borderRadius: 10,
          boxShadow: '0 1px 3px rgba(0,0,0,.12)',
        }}
      >
        <h1 style={{ margin: '0 0 .75rem', fontSize: '1.35rem' }}>This ticket link is no longer valid</h1>
        <p style={{ margin: '0 0 .75rem' }}>
          The link you followed has expired. Links sent before our latest security update no longer open.
        </p>
        <p style={{ margin: 0 }}>Please reply to the ticket email, or ask your service desk to send the link again.</p>
      </div>
    </div>
  )
}

function Attachments({ files }: { files: CustomerTicketAttachment[] }) {
  if (files.length === 0) return null

  return (
    <div>
      <h4>Attachments</h4>
      <hr className="dotted" style={{ marginTop: 5 }} />
      <ul className="attachments-list">
        {files.map((file) => (
          <li key={file.name} className={file.image ? 'attachment-image-preview' : 'attachment-file-preview'}>
            <i className={file.image ? 'fa fa-image' : 'fa fa-file'} aria-hidden="true"></i> {file.name} ({bytes(file.size)})
          </li>
        ))}
      </ul>
    </div>
  )
}

export function CustomerTicketPage({ token }: { token: string }) {
  const q = useQuery(customerTicketQuery(token))

  if (q.isError) return <LinkGone />
  if (!q.data) return <Spinner />

  const { row, description, attachments, history } = q.data

  return (
    <div id="content" style={{ padding: 15 }}>
      <table
        className="table table-striped table-hover table-bordered dataTable dataTableM"
        id="ticketTable"
        width="100%"
        style={{ paddingBottom: 10 }}
      >
        <thead>
          <tr>
            {COLUMNS.map((title) => (
              <th key={title}>{title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>{row.id}</strong>
            </td>
            <td>
              <span className="badge ticket-type-badge" title={row.type.name} style={{ backgroundColor: row.type.color }}>
                {row.type.short}
              </span>
            </td>
            <td>{row.subject}</td>
            <td>{row.company}</td>
            <td>{row.office}</td>
            <td>
              {row.affected.category === 2 ? (
                <span>
                  <i className="fas fa-desktop color-warning" aria-hidden="true"></i> device
                </span>
              ) : (
                <span>{row.affected.text}</span>
              )}
            </td>
            <td>
              <span className="label font-10" style={{ backgroundColor: row.priority.color }}>
                {row.priority.name}
              </span>
            </td>
            <td>
              <span>{row.category}</span>
            </td>
            <td>
              <span>{row.creator}</span>
            </td>
            <td>
              <span>{row.group}</span>
            </td>
            <td>
              <span>{row.assignee.name}</span>
            </td>
            <td>
              <span>{row.created}</span>
            </td>
            <td>
              <span>{row.updated}</span>
            </td>
            <td style={{ backgroundColor: row.status.color, textAlign: 'center' }}>
              <span>
                <label className="label ticket-status-label">{row.status.name}</label>
              </span>
            </td>
          </tr>
          <tr className="rowDetails">
            <td className="rowDetails" colSpan={14}>
              <div className="row" style={{ margin: 0 }}>
                <div
                  className="col-md-6"
                  style={{ padding: 0, margin: 0, backgroundColor: '#FFF', borderRight: '1px solid #ddd', marginRight: -1 }}
                >
                  <div style={{ margin: 0, padding: 20 }}>
                    <h4 style={{ marginBottom: 30, color: 'rgb(7, 144, 155)' }}>
                      <i className="fas fa-ticket-alt" aria-hidden="true"></i> {row.subject}
                    </h4>

                    <div>
                      <h4>Description</h4>
                      <hr className="dotted" style={{ marginTop: 5 }} />
                      <div style={{ marginBottom: 10 }}>
                        {description === '' ? (
                          <div
                            style={{
                              fontFamily: 'Calibri',
                              background: '#eee',
                              padding: '4px 10px',
                              borderRadius: 5,
                              fontSize: 12,
                            }}
                          >
                            No description available
                          </div>
                        ) : (
                          /*
                           * Descrierea e scrisa intr-un editor bogat si se
                           * pastreaza ca HTML - la fel ca in portal, unde
                           * trecea prin `xss_clean` si se lipea in pagina.
                           * Curatarea ramane pe server, unde era si acolo.
                           */
                          <span dangerouslySetInnerHTML={{ __html: description }} />
                        )}
                      </div>
                    </div>

                    <div>
                      <h4>Details</h4>
                      <hr className="dotted" style={{ marginTop: 5 }} />
                      <table className="details-table" width="100%">
                        <tbody>
                          <tr>
                            <td className="title">Company</td>
                            <td>
                              <i className="fa fa-building" aria-hidden="true"></i> {row.company}
                            </td>
                          </tr>
                          <tr>
                            <td className="title">Status</td>
                            <td style={{ color: row.status.color }}>
                              <i className="fa fa-flag" aria-hidden="true"></i> {row.status.name.toUpperCase()}
                            </td>
                          </tr>
                          <tr>
                            <td className="title">Type</td>
                            <td>{row.type.name}</td>
                          </tr>
                          <tr>
                            <td className="title">Priority</td>
                            <td>
                              <span className="label" style={{ backgroundColor: row.priority.color }}>
                                {row.priority.name}
                              </span>
                            </td>
                          </tr>
                          {row.category !== '' && (
                            <tr>
                              <td className="title">Category</td>
                              <td>{row.category}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <Attachments files={attachments} />
                  </div>
                </div>
                <div className="col-md-6" style={{ padding: 0, margin: 0, borderLeft: '1px solid #ddd', backgroundColor: '#FFF' }}>
                  <div style={{ margin: 0, padding: 20 }}>
                    <div className="btn-group" role="group">
                      <span style={{ fontSize: 15 }}>All operations</span>
                    </div>
                    {history && history.events.length > 0 ? (
                      <TicketChangesTimeline
                        ticket={row.id}
                        events={history.events}
                        links={false}
                        readComment={(comment) => fetchCustomerComment(token, comment)}
                      />
                    ) : (
                      <div className="note note-info ch-empty">
                        <i className="fas fa-info-circle" aria-hidden="true"></i> Nothing has happened on this ticket yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
